import React, { memo, type MouseEvent, type KeyboardEvent } from 'react';
import { useFragment, graphql } from 'react-relay';
import { Box, xcss } from '@atlaskit/primitives';
import { ff } from '@atlassian/jira-feature-flagging';
import { SingleLineTextInlineEditViewWithIsEditable } from '@atlassian/jira-issue-field-single-line-text-inline-edit-full';
import type { issueSummary_nativeIssueTable_IssueSummaryCell$key } from '@atlassian/jira-relay/src/__generated__/issueSummary_nativeIssueTable_IssueSummaryCell.graphql';
// eslint-disable-next-line jira/import/no-restricted-paths
import { useLazyLoadEditability } from '../../../../services/lazy-editability-loader';
import ErrorCell from '../../error-cell';
import TableCellLink from '../../table-cell-link';

export type Props = {
	fieldRef: issueSummary_nativeIssueTable_IssueSummaryCell$key;
	issueKey: string;
	/**
	 * Dispatched when a table cell with a link to the issue is selected. This method should return `true` to signal
	 * that the event has been handled, which will prevent default event handling.
	 */
	onIssueLinkSelected?: (issueKey: string) => boolean;
};

export const IssueSummaryCell = memo<Props>(
	({ fieldRef, issueKey, onIssueLinkSelected }: Props) => {
		const data = useFragment<issueSummary_nativeIssueTable_IssueSummaryCell$key>(
			graphql`
				fragment issueSummary_nativeIssueTable_IssueSummaryCell on JiraSingleLineTextField
				@argumentDefinitions(isInlineEditingEnabled: { type: "Boolean!" }) {
					id
					lazyIsEditableInIssueView @include(if: $isInlineEditingEnabled)
					...singleLineText_issueFieldSingleLineTextInlineEditFull_SingleLineTextInlineEditViewWithIsEditable_fragmentRef
				}
			`,
			/* eslint-enable @atlassian/relay/graphql-naming */
			fieldRef,
		);

		// If the property does not exist in fragment data then we know it has been conditionally excluded because inline
		// editing is disabled. In this case we explicitly set isEditable to false to bypass the lazy editability query.
		const isEditable = 'lazyIsEditableInIssueView' in data ? data.lazyIsEditableInIssueView : false;
		useLazyLoadEditability(data.id, isEditable);

		const renderCustomReadView = (text: string | null) => {
			if (!text) {
				return <ErrorCell cellType="summary" reason="Missing required fields" />;
			}

			// TODO: inline content when cleaning up jsv-one-issue-table-spreadsheet-ui_ygrnf
			const content = (
				<TableCellLink
					innerFocusOutline={ff('jsv-one-issue-table-spreadsheet-ui_ygrnf')}
					href={`/browse/${issueKey}`}
					onClick={(e: MouseEvent | KeyboardEvent) => {
						// Use default browser event handling when a modifier key is pressed during the click event
						const isModifierKeyUsed = e.ctrlKey || e.shiftKey || e.metaKey;
						const handled =
							!isModifierKeyUsed &&
							onIssueLinkSelected !== undefined &&
							onIssueLinkSelected(issueKey);
						if (handled) {
							e.preventDefault();
						}
					}}
				>
					{text}
				</TableCellLink>
			);

			// Span is necessary here to ensure only link text is clickable for multiline summaries, rather than the
			// entire content box.
			return ff('jsv-one-issue-table-spreadsheet-ui_ygrnf') ? (
				<Box as="span" xcss={textOverflowStyles}>
					{content}
				</Box>
			) : (
				<span>{content}</span>
			);
		};

		return (
			<SingleLineTextInlineEditViewWithIsEditable
				spacing={ff('jsv-one-issue-table-spreadsheet-ui_ygrnf') ? 'default' : undefined}
				editViewPopup={ff('nin-resizable-columns_8z4eo')}
				editViewPopupAlignBlock={ff('nin-resizable-columns_8z4eo') ? 'center' : undefined}
				fragmentRef={data}
				isEditable={isEditable ?? false}
				isRequired
				maxLength={255}
				readViewFitContainerHeight
				renderCustomReadView={renderCustomReadView}
				hideEditButton={false}
			/>
		);
	},
);

const textOverflowStyles = xcss({
	display: '-webkit-box',
	WebkitBoxOrient: 'vertical',
	WebkitLineClamp: 2,
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	wordBreak: 'break-word',
});
